import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import useComponentStyles from '../../themes/componentStyles';
import { componentSpacing } from '../../utils/spacing';
import {
  Grid, Typography, Paper, Box, CircularProgress,
  Snackbar, Alert, Button,
  IconButton, Avatar, useTheme
} from '@material-ui/core';
import UserInvoices from './UserInvoices';
import {
  profileUser as profileUserIcon,
  profileUser as editProfileIcon,
  walletCircuit as withdrawIcon,
  coinDollarGold as refillIcon,
  progressBarHalf,
  coinDollar,
  smileyWinking
} from '../../assets/images/icons';
import backend from '../utilities/Backend';
import ZoomableImage from '../../components/ZoomableImage';
import { getMediaUrl, processMediaUrls } from '../../utils/mediaUtils';
import { SOCIAL_MEDIA_PLATFORMS, formatSocialMediaUrl } from '../../components/SocialMediaIcons';
import { navigateBack, debugNavigationStack } from '../../utils/navigationUtils';
import PageHeaderButton from '../../components/Navigation/PageHeaderButton';
import TagsOnProfile from '../../components/Tags/TagsOnProfile';
import RefillBalanceDialog from '../../components/Payment/RefillBalanceDialog';
import WithdrawalDialog, { openWithdrawalDialog } from '../../components/WithdrawalDialog';
import { testIdProps } from '../../utils/testIds';
import DecorativeIcons from '../../components/DecorativeIcons';
const dashboardIcons = [
  { src: progressBarHalf, alt: 'progress', top: '5%', right: '3%', size: 36, opacity: 0.1, animation: 'float' },
  { src: coinDollar, alt: 'coin', top: '30%', left: '2%', size: 32, opacity: 0.08, animation: 'pulse' },
  { src: smileyWinking, alt: 'smiley', bottom: '15%', right: '4%', size: 30, opacity: 0.1, animation: 'float' },
];

// Glass card style helper — matches component.png gradient glow design
const glassCard = (theme) => ({
  background: theme.palette.background.paper,
  border: '1px solid rgba(167,139,250,0.15)',
  borderRadius: '12px',
  boxShadow: '0 0 20px rgba(167,139,250,0.08), inset 0 0 30px rgba(167,139,250,0.03)',
});

const Dashboard = () => {
  const classes = useComponentStyles();
  const theme = useTheme();
  const [balance, setBalance] = useState({ usd: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userProfile, setUserProfile] = useState(null);

  const [withdrawalSuccess, setWithdrawalSuccess] = useState(false);
  const [refillDialogOpen, setRefillDialogOpen] = useState(false);

  const account = useSelector((state) => state.account);
  const accountUser = account.user;
  const history = useHistory();
  const location = useLocation();
  const displayUsername = accountUser?.username || 'Loading profile...';
  const displayEmail = accountUser?.email || '';

  useEffect(() => {
    fetchBalance();
    fetchUserProfile();
  }, []);

  const fetchBalance = async () => {
    if (!account.user || !account.user._id) return;
    setLoading(true);
    setError(null);
    try {
      const balanceResponse = await backend.get('balance');
      setBalance({ usd: balanceResponse.data.balance?.usd || 0 });
    } catch (err) {
      console.error('Error fetching user payment data:', err);
      setError('Failed to load payment information');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserProfile = async () => {
    if (!account.user || !account.user._id) return;
    try {
      const response = await backend.get('v1/profile');
      setUserProfile(response.data.profile);
    } catch (err) {
      console.error('Error fetching user profile:', err);
    }
  };

  const handleWithdrawalClick = () => {
    openWithdrawalDialog();
  };

  const handleCloseSuccessMessage = () => {
    setWithdrawalSuccess(false);
  };

  const handleEditClick = () => {
    history.push('/edit-profile');
  };

  const handleCloseRefillDialog = () => {
    setRefillDialogOpen(false);
    fetchBalance();
  };

  const hasGallery = userProfile?.metadata?.additional_images && userProfile.metadata.additional_images.length > 0;

  return (
    <Grid container sx={{ ...componentSpacing.pageContainer(theme), position: 'relative' }}>
      <DecorativeIcons icons={dashboardIcons} />

      {/* Back Button */}
      <Grid item xs={12} sx={componentSpacing.pageHeader(theme)}>
        <PageHeaderButton
          onClick={() => {
            debugNavigationStack(location, 'Profile Page Navigation');
            navigateBack(history, '/search');
          }}
        />
      </Grid>

      {/* ===== PROFILE HEADER — horizontal glass card ===== */}
      <Grid item xs={12}>
        <Paper elevation={0} sx={{
          ...componentSpacing.card(theme),
          ...glassCard(theme),
          display: 'flex',
          gap: 3,
          alignItems: 'center',
          [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            textAlign: 'center',
            gap: 2,
          },
        }}>
          {/* LEFT: Avatar */}
          {userProfile && (
            <Avatar
              src={getMediaUrl(userProfile.profile_image_url)}
              alt={userProfile.username}
              sx={{
                width: 96, height: 96, flexShrink: 0,
                [theme.breakpoints.down('sm')]: { width: 72, height: 72 },
              }}
            >
              {!userProfile.profile_image_url && <img src={profileUserIcon} alt="" style={{ width: 48, height: 48, objectFit: 'contain' }} />}
            </Avatar>
          )}

          {/* CENTER: Info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h5" className={classes.userText} style={{ fontWeight: 600 }}>
              {displayUsername}
            </Typography>
            <Typography variant="body2" className={classes.staticText} style={{ marginTop: 2 }}>
              {displayEmail}
            </Typography>

            {userProfile?.tags && userProfile.tags.length > 0 && (
              <Box style={{ marginTop: 8 }}>
                <TagsOnProfile tags={userProfile.tags} />
              </Box>
            )}

            {userProfile?.introduction && (
              <Typography variant="body2" style={{ marginTop: 8, fontStyle: 'italic' }}>
                "{userProfile.introduction}"
              </Typography>
            )}

            {/* Social Media Links */}
            {userProfile?.metadata?.socialMedia && (
              <Box mt={1} display="flex" gap={1} sx={{
                [theme.breakpoints.down('sm')]: { justifyContent: 'center' },
              }}>
                {SOCIAL_MEDIA_PLATFORMS.map((platform) => {
                  const username = userProfile.metadata.socialMedia[platform.id];
                  if (!username) return null;
                  const Icon = platform.icon;
                  const url = formatSocialMediaUrl(platform.id, username);
                  return (
                    <IconButton
                      key={platform.id}
                      component="a"
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      title={`${platform.name}: @${username}`}
                    >
                      <Icon />
                    </IconButton>
                  );
                })}
              </Box>
            )}
          </Box>

          {/* RIGHT: Action buttons */}
          <Box sx={{
            display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0,
            [theme.breakpoints.down('sm')]: {
              flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center',
            },
          }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<img src={editProfileIcon} alt="" style={{ width: 20, height: 20, objectFit: 'contain' }} />}
              onClick={handleEditClick}
              {...testIdProps('button', 'dashboard', 'edit-profile')}
            >
              Edit Profile
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<img src={withdrawIcon} alt="" style={{ width: 20, height: 20, objectFit: 'contain' }} />}
              onClick={handleWithdrawalClick}
              {...testIdProps('button', 'dashboard', 'withdraw')}
            >
              Withdraw
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<img src={refillIcon} alt="" style={{ width: 20, height: 20, objectFit: 'contain' }} />}
              onClick={() => setRefillDialogOpen(true)}
              {...testIdProps('button', 'dashboard', 'refill')}
            >
              Refill
            </Button>
          </Box>
        </Paper>
      </Grid>

      {/* ===== STAT CARDS ROW ===== */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 2,
          [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: '1fr',
          },
        }}>
          {/* Balance Card */}
          <Paper elevation={0} sx={{ ...componentSpacing.card(theme), ...glassCard(theme) }}>
            <Typography variant="caption" className={classes.staticText}>Balance</Typography>
            {loading ? (
              <CircularProgress size={18} />
            ) : error ? (
              <Typography variant="body1" color="error">{error}</Typography>
            ) : (
              <Typography variant="h5" className={classes.userText} style={{ fontWeight: 700 }}>
                ${balance.usd} USD
              </Typography>
            )}
          </Paper>

          {/* Email Card */}
          <Paper elevation={0} sx={{ ...componentSpacing.card(theme), ...glassCard(theme) }}>
            <Typography variant="caption" className={classes.staticText}>Email</Typography>
            <Typography variant="body1" className={classes.userText} style={{
              wordBreak: 'break-all', fontWeight: 500
            }}>
              {displayEmail}
            </Typography>
          </Paper>

          {/* Member Card */}
          <Paper elevation={0} sx={{ ...componentSpacing.card(theme), ...glassCard(theme) }}>
            <Typography variant="caption" className={classes.staticText}>Member</Typography>
            <Typography variant="body1" className={classes.userText} style={{ fontWeight: 500 }}>
              {displayUsername}
            </Typography>
          </Paper>
        </Box>
      </Grid>

      {/* ===== TWO-COLUMN BODY: Gallery + Invoices ===== */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: hasGallery ? '7fr 5fr' : '1fr',
          gap: 3,
          [theme.breakpoints.down('md')]: {
            gridTemplateColumns: '1fr',
          },
        }}>
          {/* Gallery */}
          {hasGallery && (
            <Paper elevation={0} sx={{ ...componentSpacing.card(theme), ...glassCard(theme) }}>
              <Typography variant="h6" gutterBottom>
                Gallery
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={2} sx={{
                [theme.breakpoints.down('sm')]: {
                  gap: 1,
                  justifyContent: 'center'
                }
              }}>
                {processMediaUrls(userProfile.metadata.additional_images).map((imageUrl, index) => (
                  <ZoomableImage
                    key={index}
                    src={imageUrl}
                    alt={`Gallery ${index + 1}`}
                    style={{
                      width: 150,
                      height: 150,
                      objectFit: 'cover',
                      borderRadius: 4
                    }}
                    sx={{
                      [theme.breakpoints.down('sm')]: {
                        width: 120,
                        height: 120
                      }
                    }}
                  />
                ))}
              </Box>
            </Paper>
          )}

          {/* Invoices */}
          <Paper elevation={0} sx={{ ...componentSpacing.card(theme), ...glassCard(theme) }}>
            <UserInvoices />
          </Paper>
        </Box>
      </Grid>

      {/* Dialogs */}
      <WithdrawalDialog />
      <RefillBalanceDialog
        open={refillDialogOpen}
        onClose={handleCloseRefillDialog}
        currentBalance={balance.usd || 0}
        requiredAmount={0}
      />

      {/* Success Message */}
      <Snackbar
        open={withdrawalSuccess}
        autoHideDuration={6000}
        onClose={handleCloseSuccessMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSuccessMessage} severity="success">
          Withdrawal successful! Your funds have been sent.
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default Dashboard;
